import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/auth';
import Wordmark from '../components/Wordmark';
import { APP_TAGLINE } from '../config/labels';

type Mode = 'login' | 'forgot';

const Login: React.FC = () => {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setInfo('');
    setPassword('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[Login] Form submitted', { email, password: password ? '***' : 'empty' });
    setError('');
    setLoading(true);

    try {
      console.log('[Login] Calling login function');
      await login({ email, password });
      console.log('[Login] Login successful, navigating to dashboard');
      navigate('/dashboard');
    } catch (err: any) {
      console.error('[Login] Login failed:', err);
      setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
      console.log('[Login] Login attempt completed');
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      await authApi.requestPasswordReset(email);
    } catch {
      // Intentionally ignored: response is the same regardless of whether the
      // address exists, so user enumeration is not possible from this form.
    } finally {
      setLoading(false);
      setInfo(
        'Falls ein Konto mit dieser E-Mail-Adresse existiert, wurde eine Anfrage zum Zurücksetzen des Passworts gesendet.',
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center">
            <Wordmark className="text-3xl" />
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {mode === 'login' ? APP_TAGLINE : 'Passwort zurücksetzen'}
          </p>
        </div>

        {mode === 'login' ? (
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">
                  E-Mail-Adresse
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="E-Mail-Adresse"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Passwort
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Passwort"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Passwort vergessen?
              </button>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleForgot}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            {info && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {info}
              </div>
            )}
            <p className="text-sm text-gray-600">
              Geben Sie Ihre E-Mail-Adresse ein. Wenn ein Konto mit dieser Adresse existiert,
              senden wir Ihnen Anweisungen zum Zurücksetzen Ihres Passworts.
            </p>
            <div>
              <label htmlFor="reset-email" className="sr-only">
                E-Mail-Adresse
              </label>
              <input
                id="reset-email"
                name="email"
                type="email"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="E-Mail-Adresse"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !!info}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Wird gesendet...' : 'Neues Passwort anfordern'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Zurück zur Anmeldung
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
